import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Asterisk } from "lucide-react";
import { HERO, SITE } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const easeEditorial = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`mask-line ${className}`}>
      <motion.span
        className="block will-change-transform"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.3, delay, ease: easeEditorial }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const headlineY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const headlineOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const imageY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const imageRotate = useTransform(scrollYProgress, [0, 1], [0, -4]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden px-6 md:px-12 lg:px-16 pt-28 md:pt-36 lg:pt-40 pb-20 md:pb-28 min-h-[92vh] flex flex-col justify-between"
    >
      {/* Signature on-load curtain */}
      <motion.div
        aria-hidden
        className="fixed inset-0 z-[80] bg-black pointer-events-none"
        initial={{ y: 0 }}
        animate={{ y: "-100%" }}
        transition={{ duration: 1, delay: 0.2, ease: easeEditorial }}
      />

      {/* Top meta row */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.1 }}
        className="grid grid-cols-12 gap-6 mb-14 md:mb-20"
      >
        <div className="col-span-6 md:col-span-3">
          <p
            data-testid="hero-meta-location"
            className="text-[11px] uppercase tracking-[0.22em] text-neutral-500"
          >
            {SITE.location}
          </p>
        </div>
        <div className="col-span-6 md:col-span-3 md:col-start-10 md:text-right">
          <p className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">
            Portfolio — MMXXV
          </p>
        </div>
      </motion.div>

      {/* Kinetic headline with parallax drift */}
      <motion.div
        style={{ y: headlineY, opacity: headlineOpacity }}
        className="relative"
      >
        <h1
          data-testid="hero-headline"
          className="font-serif font-light tracking-tighter leading-[0.94] text-[clamp(3.25rem,11vw,11rem)]"
        >
          <MaskedLine delay={0.55}>Brand</MaskedLine>
          <MaskedLine delay={0.67} className="pl-[8vw] md:pl-[10vw]">
            <span className="italic font-light">Storyteller</span>
          </MaskedLine>
          <MaskedLine delay={0.79} className="pl-[18vw] md:pl-[22vw]">
            &amp; Strategist<span className="italic font-light">.</span>
          </MaskedLine>
        </h1>

        {/* Rotating signature mark */}
        <motion.div
          aria-hidden
          className="absolute right-0 -top-8 md:-top-4 text-black"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 1.5, ease: easeEditorial }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
          >
            <Asterisk
              className="w-10 h-10 md:w-16 md:h-16"
              strokeWidth={0.75}
            />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Subheading + clipped editorial frame */}
      <div className="grid grid-cols-12 gap-6 mt-16 md:mt-24 items-end">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15, ease: easeEditorial }}
          className="col-span-12 md:col-span-6"
        >
          <p
            data-testid="hero-subheading"
            className="font-serif text-xl sm:text-2xl md:text-[1.7rem] leading-snug text-neutral-800 max-w-[44ch]"
          >
            {HERO.subheading}
          </p>

          <div className="mt-10 flex items-center gap-6">
            <button
              data-testid="hero-cta-work"
              onClick={() => scrollToId("work")}
              className="group inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] font-medium"
            >
              <span className="link-underline">Selected Work</span>
              <span
                aria-hidden
                className="inline-block transition-transform duration-500 group-hover:translate-x-1"
              >
                →
              </span>
            </button>
            <a
              data-testid="hero-cta-contact"
              href={`mailto:${SITE.email}`}
              className="text-[11px] uppercase tracking-[0.22em] font-medium link-underline"
            >
              Get in touch
            </a>
          </div>
        </motion.div>

        {/* Deliberately treated photograph — clipped frame, slow reveal, parallax */}
        <motion.div
          style={{ y: imageY, rotate: imageRotate }}
          className="col-span-8 col-start-3 md:col-span-4 md:col-start-9 mt-12 md:mt-0"
        >
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, delay: 1.05, ease: easeEditorial }}
            className="overflow-hidden aspect-[3/4] bg-neutral-100 border border-black/10"
          >
            <motion.img
              src="/portfolio/image15.png"
              alt="Golden Goose — Marathon campaign still"
              initial={{ scale: 1.15 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.6, delay: 1.05, ease: easeEditorial }}
              className="w-full h-full object-cover grayscale-[15%]"
            />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.9 }}
            className="mt-3 text-[10px] uppercase tracking-[0.22em] text-neutral-500 flex justify-between"
          >
            <span>Golden Goose — Marathon</span>
            <span>Fig. 01</span>
          </motion.p>
        </motion.div>
      </div>

      {/* Vertical scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 2 }}
        className="hidden lg:flex absolute left-6 bottom-10 items-center gap-2"
      >
        <div className="h-12 w-px bg-black/40" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          Scroll
        </span>
      </motion.div>
    </section>
  );
}
